import React, { useState, useEffect } from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import { StyledTypography, DropdownContainer, StyledListItemText, DropdDownGrid } from './DropdownStyles.Styled';
import { StyledCheckbox, StyledRadioCheckbox } from '../../../utils/commonStyles.Styled';
import { getDropdownStyles } from '../../../utils/helper';

interface DynamicDropdownProps {
  label: string;
  options: string[];
  selectedOptions: string[];
  multiple?: boolean;
  loading?: boolean;
  onChange: (selected: string[]) => void;
  onApply?: () => Promise<void>;
  customStyle: any;
  borderColor: any;
  disabled?: boolean;
}

export const DynamicDropdown: React.FC<DynamicDropdownProps> = ({
  label,
  options,
  selectedOptions,
  multiple = true,
  loading = false,
  onChange,
  onApply,
  disabled = false,
  borderColor,
  customStyle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionSelectedOptions, setSessionSelectedOptions] = useState<string[]>(selectedOptions);

  useEffect(() => {
    setSessionSelectedOptions(selectedOptions);
  }, [selectedOptions]);

  const handleSelect = (option: string) => {
    let newSelectedOptions;

    if (multiple) {
      const isChecked = sessionSelectedOptions.includes(option);
      newSelectedOptions = isChecked
        ? sessionSelectedOptions.filter((o) => o !== option)
        : [...sessionSelectedOptions, option];
    } else {
      newSelectedOptions = [option];
    }

    setSessionSelectedOptions(newSelectedOptions);
    if (!multiple) {
      onChange(newSelectedOptions);
    }
  };

  const handleClose = async () => {
    setIsOpen(false);
    if (JSON.stringify(sessionSelectedOptions) !== JSON.stringify(selectedOptions)) {
      if (multiple) {
        onChange(sessionSelectedOptions);
      }
      if (onApply) {
        await onApply();
      }
    }
  };

  return (
    <FormControl
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: '10px',
      }}
      disabled={loading || disabled}
    >
      <StyledTypography>{label}: </StyledTypography>
      <Select
        style={{
          background: 'white',
          borderRadius: '3.81px',
          border: `1px solid ${borderColor} !important`,
          borderBottom: `0.8px solid ${borderColor} !important`,
          fontSize: '9px',
          color: 'black',
          ...customStyle,
        }}
        multiple={multiple}
        value={multiple ? sessionSelectedOptions : sessionSelectedOptions[0] || ''}
        onClose={handleClose}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        MenuProps={{
          PaperProps: {
            style: {
              width: '190px',
              fontSize: '9px',
              borderRadius: '10px',
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
        renderValue={(selected) => {
          if (multiple) {
            if (Array.isArray(selected)) {
              if (selected.length === 1) {
                return selected[0];
              } else if (selected.length === options.length) {
                return 'All';
              } else {
                return `Multiple`;
              }
            }
            return '';
          } else {
            return selected;
          }
        }}
        sx={getDropdownStyles(borderColor)}
      >
        <DropdownContainer style={{ flexDirection: 'row', marginTop: '10px' }}>
          <DropdDownGrid>
            {options.map((option) => (
              <MenuItem style={{ padding: '5px 0px' }} key={option} value={option} onClick={() => handleSelect(option)}>
                {multiple ? (
                  <StyledCheckbox value={option} checked={sessionSelectedOptions.includes(option)} />
                ) : (
                  <StyledRadioCheckbox value={option} checked={sessionSelectedOptions.includes(option)} />
                )}
                <StyledListItemText primary={option} />
              </MenuItem>
            ))}
          </DropdDownGrid>
        </DropdownContainer>
      </Select>
    </FormControl>
  );
};
